import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { LOGGED_IN_MENU_DYNAMIC_QUERY } from './operations';
import LoggedInMenuHeader from './LoggedInMenuHeader';
import { FormCell } from '../FormStructure';
import RecommendationSlidersContainer from '../Recommendations/RecommendationSlidersContainer';
import { ACCOUNT_NAV_RECS_TARGET_ID } from './constants';

const propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  text: PropTypes.object.isRequired,
};

/**
 * @component LoggedInMenuDynamic
 * @description Handles dynamic data fetching and rendering for the logged in menu
 * @param {Object} props - Component props
 * @param {Object} props.text - Static text data from parent component
 * @param {string} props.loyaltyTierImageSrc - Loyalty tier image source URL
 */
export default function LoggedInMenuDynamic({ text }) {
  const {
    loading,
    data = {},
    refetch,
  } = useQuery(LOGGED_IN_MENU_DYNAMIC_QUERY, {
    ssr: false,
    context: { batch: true },
    fetchPolicy: 'cache-and-network', // Use cache but also fetch fresh data
    refetchPolicy: 'network-only', // Always refetch on user action
  });

  useEffect(() => {
    const refetchCallback = () => refetch();
    document.addEventListener('myInfo:updated', refetchCallback);
    return () => document.removeEventListener('myInfo:updated', refetchCallback);
  }, [refetch]);

  const { textFor = {}, config = {} } = data;
  const textStore = { ...text, ...textFor };
  const { loyaltyTierImageSrc = '' } = config;

  const recsTarget = document.getElementById(ACCOUNT_NAV_RECS_TARGET_ID);
  const isLoading = loading && !data?.textFor;

  const {
    hasSignInRecsEnabled = false,
  } = data;

  const recommendationSliders = (
    <FormCell isLoading={isLoading}>
      <RecommendationSlidersContainer
        clientId="account-flyout-1"
        placements={['generic_page.account_flyout1']}
        // Check the cache first for the account-flyout-1 placements
        queryOptions={{ fetchPolicy: 'cache-first' }}
        useGrid
      />
    </FormCell>
  );

  return (
    <>
      <FormCell isLoading={isLoading}>
        <LoggedInMenuHeader
          text={textStore}
          loyaltyTierImageSrc={loyaltyTierImageSrc}
          isLoading={isLoading}
        />
      </FormCell>
      {
        hasSignInRecsEnabled
        && recsTarget
        && (
          createPortal(
            recommendationSliders,
            recsTarget,
          )
        )
      }
    </>
  );
}

LoggedInMenuDynamic.propTypes = propTypes;
